import React, { Component, createRef } from "react";
import Playlist from "./Playlist";
import Waveform from "../Waveform";
import DownloadItem from "../products/DownloadItem";
import FavoriteButton from "../products/FavoriteButton";
import Cover from "../products/Cover";
import { inject, observer } from "mobx-react";
import { observe } from "mobx";
import filtersStore from "../../stores/filtersStore";
import history from "../common/history.js";
import BuyItem from "../products/BuyItem";
import BuyButton from "./BuyButton";
import { ReactComponent as Pause } from "#images/pause.svg";
import { ReactComponent as Play } from "#images/play.svg";
import Loader from "#images/loader.gif";
import ArrowUpGray from "#images/arrow-top-gray.svg";
import ArrowDownGray from "#images/arrow-down-gray.svg";
import SpeakerOff from "#images/speaker-off.svg";
import SpeakerOne from "#images/speaker-on-1.svg";
import SpeakerTwo from "#images/speaker-on-2.svg";
import { ReactComponent as ProductItemPoint } from "#images/product-item-point.svg";
import { ReactComponent as Cancel } from "#images/white-x.svg";
import { ReactComponent as Arrow } from "#images/back-to-beatport-arrow.svg";

@inject("playlistStore", "packsStore", "packStore", "authStore")
@observer
class Player extends Component {
  constructor(props) {
    super(props);
    this.state = {
      playlistExpanded: false,
      audioLoading: false,
      intervalId: null,
      currentTime: 0,
      trackurl: null,
      playing: false,
      audioPlayed: false,
    };

    this.disposers = [];
    this.audioPlayed = false;

    this.volumeRef = createRef();
    this.volumeIconRef = createRef();
    this.waveformRef = createRef();
    this.playButtonRef = createRef();
    this.audioRef = createRef();
    this.playerRef = createRef();
  }

  componentDidMount() {
    this.setupAudio();
    this.updateVolume();
    this.audio.preload = "auto";
    document.body.addEventListener("keypress", this.handleKeyPress);
    document.body.addEventListener("keydown", this.handleKeyDown);

    const { playlistStore } = this.props;
    const { audioLoading } = this.state;

    this.disposers.push(
      observe(this.props.playlistStore, "isPlaying", (change) => {
        console.debug("Player observe, isPlaying", change.newValue);

        if (change.newValue) {
          // play empty.mp3
          if (!this.audioPlayed) {
            console.debug("playing empty...");
            this.audio.play().catch((e) => {
              console.log(e);
            });
            this.audioPlayed = true;
          }

          if (this.currentlyPlays) {
            if (!audioLoading && !this.state.intervalId) {
              this.setState({ intervalId: setInterval(this.timeUpdate, 100) });
            }
          }
        } else {
          this.audio.pause();

          if (this.state.intervalId) {
            clearInterval(this.state.intervalId);
            this.setState({ intervalId: null });
          }
        }
      }),
    );

    this.disposers.push(
      observe(playlistStore, "isPlaying", (change) => {
        console.warn("observe, isPlaying 2: ", change);

        if (change.newValue) {
          this.setState({
            playing: true,
            trackurl: playlistStore.currentAudio,
          });
        } else {
          this.setState({ playing: false });
          this.audio.pause();
        }
      }),
    );

    this.disposers.push(
      observe(this.props.playlistStore, "currentAudio", (change) => {
        console.warn("observe, current audio: ", change);

        const track = change.newValue;
        if (track) {
          if (track !== this.state.trackurl) {
            console.debug("observe currentAudio got new track:", track);
            this.setState({ trackurl: track });
          }
        } else {
          console.debug("observe currentAudio no track?");
          this.audio.pause();
        }
      }),
    );

    document.body.addEventListener("click", this.handleBodyClick.bind(this));
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (this.state.trackurl != prevState.trackurl && this.state.playing) ||
      (this.state.playing && !prevState.playing && this.audio.src !== "")
    ) {
      this.audio.play().catch((e) => {
        console.log(e);
      });
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener("keypress", this.handleKeyPress);
    document.body.removeEventListener("keydown", this.handleKeyDown);
    document.body.removeEventListener("click", this.handleBodyClick);

    this.disposers.map((d) => d());
  }

  // Hide playlist if user clicked outside of player area (player + playlist)
  handleBodyClick(e) {
    if (
      this.state.playlistExpanded &&
      !(
        e.target === this.playerRef.current ||
        this.playerRef.current.contains(e.target)
      )
    ) {
      this.setState({ playlistExpanded: false });
    }
  }

  setupAudio() {
    const player = this.audioRef.current;
    player.ontimeupdate = this.timeUpdate;
    player.onloadstart = this.audioLoading; // not supported by ogv
    player.onlaodeddata = this.audioLoaded; // found some code in ogv on this event, but it's not being fired
    player.onloadedmetadata = this.audioLoaded; // supported by ogv
    player.onended = this.audioEnded;
    // document.body.appendChild(player);
    this.ogvPlayer = player;
  }

  // Playing audio
  play() {
    this.props.playlistStore.play();
    this.audio.play().catch((e) => {
      console.log(e);
    });
  }

  togglePlay = () => {
    const { playlistStore } = this.props
    if (playlistStore.isPlaying) {
      playlistStore.pause()
    } else {
      playlistStore.play()
    }
  };

  handleKeyPress = (e) => {
    // Allow to toggle play by clicking Space key
    if (e.target.tagName == "INPUT") {
      return;
    }
    if (e.target.tagName == "TEXTAREA") {
      return;
    }
    const keys = [32];
    if (keys.indexOf(e.which) != -1) {
      e.preventDefault();
    }
    switch (e.which) {
      case 32:
        if (e.target != this.playButtonRef.current) {
          // Prevent pressing Space on play button - double event
          this.togglePlay();
        }
        break;
    }
  };

  handleKeyDown = (e) => {
    if (e.target.tagName == "INPUT") {
      return;
    }
    if (e.target.tagName == "TEXTAREA") {
      return;
    }
    const keys = [38, 40];
    if (keys.indexOf(e.which) != -1) {
      e.preventDefault();
    }
    switch (e.which) {
      case 38:
        this.props.playlistStore.playPrevious();
        break;
      case 40:
        this.props.playlistStore.playNext();
        break;
    }
  };

  // Rewinding by clicking on waveform
  enableRewind = (e) => {
    this.props.playlistStore.rewindMode = true;
    this.rewindTo(e);
    this.play();
  };

  disableRewind = () => {
    this.props.playlistStore.rewindMode = false;
  };

  rewindTo = (e) => {
    if (e.type == "mousemove" && !this.props.playlistStore.rewindMode) {
      return;
    }
    const canvas = this.waveformRef.current.refs.canvas;
    const canvasPositionX = this.calculatePositionX(canvas);
    const canvasClickPositionX = e.clientX - canvasPositionX;
    const clickedPercent = canvasClickPositionX / canvas.width;
    this.audio.currentTime = this.audio.duration * clickedPercent;
  };

  // Controlling volume
  updateVolume = () => {
    const volume = this.volumeRef.current.value;
    this.audio.volume = volume / 100;
    const bg =
      "linear-gradient(to right, #FAC300 0%, #FAC300 " +
      volume +
      "%, #2E2E2E " +
      volume +
      "%, #2E2E2E 100%)";
    this.volumeRef.current.style.setProperty("--track-bg", bg);
    this.updateVolumeIcon();
  };

  updateVolumeIcon() {
    var iconSrc, className;
    if (this.audio.volume < 0.5) {
      if (this.audio.volume == 0) {
        iconSrc = SpeakerOff;
      } else {
        iconSrc = SpeakerOne;
      }
    } else {
      iconSrc = SpeakerTwo;
    }
    return (this.volumeIconRef.current.src = iconSrc);
  }

  toggleMute = () => {
    const currentVolume = this.volumeRef.current.value;
    if (currentVolume == 0) {
      this.volumeRef.current.value = this.props.playlistStore.rememberedVolume;
    } else {
      this.props.playlistStore.rememberedVolume = currentVolume;
      this.volumeRef.current.value = 0;
    }
    this.updateVolume();
  };

  // Shortcuts
  get currentlyPlays() {
    return this.props.playlistStore.currentlyPlays;
  }

  get currentAudio() {
    return this.props.playlistStore.currentAudio;
  }

  get audioKey() {
    return this.currentlyPlays &&
      this.currentlyPlays.key &&
      this.currentlyPlays.key.length
      ? this.currentlyPlays.key[0].name
      : "";
  }

  get audio() {
    return this.audioRef.current;
  }

  get isPlaylistEmpty() {
    return this.props.playlistStore.items.length == 0;
  }

  redirectToProduct(_e) {
    let params = filtersStore.toParams();
    history.push(`/product/${this.currentlyPlays.product_id}?${params}`);
  }

  // Draw blue waveform while playing
  timeUpdate = () => {
    console.debug("audio => timeUpdate...");

    const { error } = this.audio;
    if (error) {
      console.error("Player error: ", error);
      this.props.playlistStore.isPlaying = false;
      this.setState({ currentTime: 0, audioLoading: false, trackurl: null });
      return;
    }

    const { trackurl, playing } = this.state;

    if (playing && trackurl && this.audio.currentTime >= 0) {
      this.setState({
        currentTime: this.audio.currentTime,
        duration: this.audio.duration,
      });
    }
  };

  // Audio loading
  audioLoaded = () => {
    this.setState({
      audioLoading: false,
      intervalId: this.state.intervalId || setInterval(this.timeUpdate, 100),
    });
  };

  audioLoading = () => {
    this.setState({ audioLoading: true });
  };

  audioEnded = () => {
    this.props.playlistStore.isPlaying = false;
    this.setState({ currentTime: 0 });
    this.timeUpdate();
  };

  // Playlist
  togglePlaylist = () => {
    this.setState((state) => {
      return { ...state, playlistExpanded: !state.playlistExpanded };
    });
  };

  clearPlaylist = () => {
    this.props.playlistStore.clear();
    this.onPlaylistCleared();
  };

  onPlaylistCleared = () => {
    this.setState({ playlistExpanded: false });
  };

  togglePlaylistButton = (kind) => (
    <div className="mx-4 flex items-center 2xl:ml-auto">
      <Arrow
        width="20"
        height="20"
        className={`[&_path]:fill-orange-1 cursor-pointer ${kind === "up" ? "rotate-90" : "-rotate-90"}`}
        onClick={this.togglePlaylist}
      />
    </div>
  );

  render() {
    const { playlistStore } = this.props;
    const { trackurl = "/empty.mp3" } = this.state;
    const current = this.currentlyPlays;
    const previewText =
      current && current.demo_for ? "Product Preview" : "Preview Quality";
    const withWatermark = current && !current.owned && !current.demo_for;
    const waveformPresent = (this.currentlyPlays?.encodedData || "").length > 4;

    return (
      <div
        className="bg-gray-15 fixed bottom-0 left-0 right-0 flex touch-none flex-col"
        ref={this.playerRef}
      >
        <audio ref={this.audioRef} src={trackurl} />

        {this.state.playlistExpanded && (
          <Playlist onCleared={this.onPlaylistCleared} />
        )}

        <div className="z-[1] flex">
          <button
            ref={this.playButtonRef}
            onClick={this.togglePlay}
            type="button"
            className="flex max-h-[80px] min-h-[80px] min-w-[80px] max-w-[80px] lg:max-h-[70px] lg:min-h-[70px] lg:min-w-[70px] lg:max-w-[70px]"
          >
            {this.currentlyPlays &&
            (this.state.audioLoading || playlistStore.isLoading) ? (
              <img src={Loader} className="object-contain" />
            ) : (
              <div
                className={`group w-full ${playlistStore.currentlyPlays ? "bg-orange-1" : "bg-gray-11"}`}
              >
                {playlistStore.isPlaying ? (
                  <Pause
                    className={`max-h-[80px] w-full lg:max-h-[70px] [&_path]:scale-[1.2] lg:[&_path]:scale-100 ${playlistStore.currentlyPlays ? "[&_path]:fill-[white]" : "[&_path]:fill-gray-4 group-hover:[&_path]:fill-orange-1"}`}
                  />
                ) : (
                  <Play
                    className={`max-h-[80px] w-full lg:max-h-[70px] lg:[&_path]:scale-90 ${playlistStore.currentlyPlays ? "[&_path]:fill-[white]" : "[&_path]:fill-gray-4 group-hover:[&_path]:fill-orange-1"}`}
                  />
                )}
              </div>
            )}
          </button>

          {this.currentlyPlays && (
            <div
              className={`relative grid grow ${this.currentlyPlays.cover ? "grid-cols-[80px_auto_auto_min-content]" : "grid-cols-[auto_auto_auto_min-content]"} grid-rows-[repeat(2,auto)] lg:flex`}
            >
              <BuyButton />

              {this.currentlyPlays.cover && (
                <div className="row-span-2">
                  <Cover
                    onClick={this.redirectToProduct.bind(this)}
                    cover={this.currentlyPlays.cover}
                  />
                </div>
              )}

              <div
                className={`${this.currentlyPlays.cover ? "col-start-2" : "col-start-1"} col-end-5 pl-4 lg:relative lg:items-center lg:pr-4 ${waveformPresent ? "" : "lg:w-[170px] xl:w-[235px]"} ${!this.currentlyPlays.cover && !waveformPresent ? "lg:hidden" : "lg:flex"}`}
              >
                {waveformPresent && (
                  <Waveform
                    ref={this.waveformRef}
                    encodedData={this.currentlyPlays.encodedData}
                    currentTime={this.state.currentTime}
                    duration={this.state.duration}
                    onMouseUp={this.disableRewind}
                    onMouseDown={this.enableRewind}
                    onMouseMove={this.rewindTo}
                    className={`hidden lg:flex lg:max-h-[29px] lg:w-[170px] xl:w-[235px]`}
                  />
                )}

                <div className="text-gray-7 text-[13px] lg:absolute lg:bottom-[1px] lg:text-xs">
                  {previewText}
                  {withWatermark && " with Watermark"}
                </div>
              </div>

              <div
                className={`row-start-1 row-end-2 hidden pl-4 lg:pl-0 ${this.currentlyPlays.cover ? "col-start-2" : "col-start-1 lg:pl-4"} col-end-3 items-center sm:flex lg:pr-4 ${!this.currentlyPlays.cover && !waveformPresent ? "lg:flex-col lg:items-start lg:justify-center" : ""}`}
              >
                <span className="line-clamp-1 lg:break-all">
                  {this.currentlyPlays.name}
                </span>
                <div
                  className={`text-gray-7 hidden text-[13px] lg:absolute lg:bottom-[5px] lg:text-xs ${!this.currentlyPlays.cover && !waveformPresent ? "lg:block" : ""}`}
                >
                  {previewText}
                  {withWatermark && " with Watermark"}
                </div>
              </div>

              {this.currentlyPlays.bpm && (
                <div className="hidden items-center lg:flex">
                  <span className="px-1.5 py-1.5 text-xs">
                    {this.currentlyPlays.bpm}
                  </span>
                </div>
              )}

              {this.audioKey && (
                <div className="hidden items-center lg:flex">
                  <span
                    className={`bg-gray-11 rounded-sm px-1.5 py-1.5 text-xs ${this.currentlyPlays?.bpm ? "ml-1" : ""}`}
                  >
                    {this.audioKey}
                  </span>
                </div>
              )}

              {
                <div
                  className={`${this.currentlyPlays.cover ? "col-start-2" : "col-start-1"} sm:cold-end-4 col-end-3 row-start-1 row-end-2 pl-4 pt-2.5 sm:col-start-3 sm:ml-auto sm:mr-4 lg:mx-0 lg:mr-2 lg:flex lg:items-center lg:p-0 ${this.currentlyPlays?.bpm || this.audioKey ? "lg:ml-3.5 xl:ml-5" : ""}`}
                >
                  {!this.currentlyPlays.owned &&
                    (this.currentlyPlays.points !== null ||
                      this.currentlyPlays.points == null) && (
                      <div className="lg:hidden">
                        <BuyItem item={this.currentlyPlays} />
                      </div>
                    )}

                  {this.currentlyPlays.owned && (
                    <DownloadItem
                      item={this.currentlyPlays}
                      yellowLabel={this.currentlyPlays.yellowLabel}
                    />
                  )}
                </div>
              }

              {this.props.authStore.isLoggedIn &&
                !this.currentlyPlays.demo_for && (
                  <div className="col-start-4 col-end-5 row-start-1 row-end-2 pr-4 pt-2.5 lg:mr-2 lg:flex lg:items-center lg:p-0 xl:ml-5">
                    <FavoriteButton
                      item={this.currentlyPlays}
                      stateMode={true}
                    />
                  </div>
                )}

              {!this.currentlyPlays.owned && current.points != null && (
                <div className="hidden items-center lg:flex xl:ml-5">
                  <span className="text-xs">{this.currentlyPlays.points}</span>
                  <ProductItemPoint className="[&_path]:fill-gray-3 ml-1" />
                </div>
              )}
            </div>
          )}

          {!this.currentlyPlays && (
            <div className="flex flex-col justify-evenly px-4 py-2.5 lg:flex-row lg:items-center">
              <span className="">Nothing selected.</span>
              <span className="text-gray-8 ml-1 hidden lg:inline-block">
                Use arrows or select sounds with mouse preview.
              </span>
              <span className="text-gray-8 lg:hidden">
                Tap sounds to preview.
              </span>
              <div className="flex">
                <img
                  className="ml-2 hidden max-h-[14px] max-w-[13px] lg:block"
                  src={ArrowUpGray}
                />
              </div>
              <div className="flex">
                <img
                  className="ml-1 hidden max-h-[14px] max-w-[13px] lg:block"
                  src={ArrowDownGray}
                />
              </div>
            </div>
          )}

          <div className="ml-auto hidden min-w-max pl-2 lg:flex">
            <div className="mr-4 flex h-full items-center">
              <div className="group relative flex min-w-max">
                <input
                  type="range"
                  ref={this.volumeRef}
                  onChange={this.updateVolume}
                  onInput={this.updateVolume}
                  className="volume bg-gray-12 absolute top-[-115px] hidden h-[56px] w-[175px] -translate-x-[calc(50%-28px)] rotate-[270deg] rounded-r pl-5 pr-3 group-hover:flex"
                />
                <img
                  src={SpeakerTwo}
                  ref={this.volumeIconRef}
                  onClick={this.toggleMute}
                  className="group-hover:bg-gray-12 min-w-[40px] cursor-pointer rounded-b px-2 py-2"
                />
              </div>
            </div>

            {!this.isPlaylistEmpty && (
              <div className="bg-gray-11 flex items-center px-2 2xl:w-[230px]">
                <div className="hidden lg:block">
                  <div className="flex">
                    <span className="hidden xl:inline-block">
                      {this.state.playlistExpanded ? "Hide" : "View"}&nbsp;
                    </span>
                    <span className="capitalize xl:normal-case">selection</span>
                    <span className="text-orange-1 ml-2 font-bold">
                      {this.props.playlistStore.items.length}
                    </span>
                  </div>
                  <div
                    className="mt-1 flex w-fit cursor-pointer items-center rounded-[20px] border border-2 px-2.5"
                    onClick={this.clearPlaylist}
                  >
                    <span>Clear</span>
                    <Cancel width="10" height="10" className="ml-2" />
                  </div>
                </div>

                {this.state.playlistExpanded &&
                  this.togglePlaylistButton("down")}

                {!this.state.playlistExpanded &&
                  this.currentlyPlays &&
                  this.togglePlaylistButton("up")}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Helper method to calculate position of given element on page
  calculatePositionX(el) {
    var xPosition = 0;

    while (el) {
      if (el.tagName == "BODY") {
        // deal with browser quirks with body/window/document and page scroll
        var xScrollPos = el.scrollLeft || document.documentElement.scrollLeft;

        xPosition += el.offsetLeft - xScrollPos + el.clientLeft;
      } else {
        xPosition += el.offsetLeft - el.scrollLeft + el.clientLeft;
      }

      el = el.offsetParent;
    }
    return xPosition;
  }
}

export default Player;
